// Sequential Task Utilities
// This module handles all logic related to sequential tasks (tasks numbered like "1. Task", "2. Task", etc.)
// including detection, numbering, completion blocking, and reordering.

import { buildTaskTree, getSubtaskStats } from './taskUtils';

// Check if a task text starts with a sequential number pattern (e.g., "1. Task")
export function isSequentialTask(task) {
  if (!task || !task.text) return false;
  const trimmedText = task.text.trim();
  const match = trimmedText.match(/^(\d+)\.\s?/);
  return match !== null;
}

// Extract the sequence number from a sequential task
export function getSequenceNumber(task) {
  if (!task || !task.text) return null;
  const trimmedText = task.text.trim();
  const match = trimmedText.match(/^(\d+)\.\s?/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

// Get all sequential tasks in the same group (siblings)
export function getSequentialTaskGroup(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return [];
  
  if (!isSequentialTask(currentTask)) return [];
  
  const siblings = tasks.filter(t => {
    if (currentTask.parent_task_id === null) {
      return t.parent_task_id === null;
    }
    return t.parent_task_id === currentTask.parent_task_id;
  });
  
  // Get all sequential tasks from siblings
  const sequentialSiblings = siblings.filter(t => isSequentialTask(t));
  
  return sequentialSiblings;
}

/**
 * Finds the contiguous sequential list (siblings with "N. " pattern) that includes the given task,
 * then returns updates to fix any wrong numbers in that list (1, 2, 3, ...).
 * Used after drop, edit, etc. to normalize numbering.
 *
 * @param {Object} task - The task to renumber around (e.g. dropped task, edited task)
 * @param {Array} tasks - All tasks (should reflect current state, e.g. after drop)
 * @param {Object} [options] - Optional. movedUp: true if task was moved to an earlier position (e.g. drag up); false if moved to a later position. When task is immediately before a "1.", movedUp true => make task first of that list; movedUp false => treat as end of list above, don't include the "1." list. expandLeft: when false, do not expand the block to the left (used when renumbering only the tail after indent). minNumber: when provided, use as the first number in the block (e.g. 2 when renumbering 3., 4. to 2., 3. after indent).
 * @returns {Array<{id: string, text: string}>} - Updates for tasks whose number was wrong
 */
export function renumberSequentialListAroundTask(task, tasks, options = {}) {
  const { movedUp, expandLeft = true, minNumber } = options;
  if (!task || !tasks) return [];

  const parentId = task.parent_task_id;
  const siblings = tasks.filter(t =>
    (parentId == null ? t.parent_task_id == null : t.parent_task_id === parentId)
  );

  siblings.sort((a, b) => {
    const orderA = a.order != null ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order != null ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.created_at || '').localeCompare(b.created_at || '');
  });

  const taskIndex = siblings.findIndex(t => t.id === task.id);
  if (taskIndex === -1) return [];

  // Check: is this task or any sibling before/after part of a sequential-looking list?
  const hasSequentialNeighbor = siblings.some(t => isSequentialTask(t));
  if (!hasSequentialNeighbor) return [];

  // Find contiguous block of siblings with "number." that includes the input task.
  // When expanding left, include a "1." as the start of this list then stop.
  // When expanding right: if the next task is "1." and it's immediately after our task: movedUp (or undefined) => include that list (task becomes first); movedUp false => don't include (task is at end of list above). Otherwise a "1." starts a different list, so stop.
  let start = taskIndex;
  let end = taskIndex;
  if (expandLeft) {
    while (start > 0) {
      const prev = siblings[start - 1];
      if (!isSequentialTask(prev)) break;
      const prevNum = getSequenceNumber(prev);
      const taskNum = getSequenceNumber(task);
      if (prevNum === 1) {
        start--; // include this "1." as the left boundary of our list
        break;
      }
      // When moved up (or unknown): e.g. we're at "2. b", prev is "7. y" — different list (6. 7.), don't include. When moved down: we're at end of our list (e.g. 6. x after 7. y), so include same-list tasks to the left (7. y).
      if (movedUp !== false && prevNum != null && taskNum != null && prevNum > taskNum + 1) {
        break;
      }
      start--;
    }
  }
  while (end < siblings.length - 1) {
    const next = siblings[end + 1];
    if (!isSequentialTask(next)) break;
    if (getSequenceNumber(next) === 1) {
      if (end === taskIndex) {
        // "1." is immediately after our task — include only if moved up (user intent: first of that list); if moved down, we're at end of list above
        if (movedUp !== false) {
          end++;
        } else {
          break;
        }
      } else {
        break; // different list starts here
      }
    } else {
      end++;
    }
  }

  const block = siblings.slice(start, end + 1);
  if (block.length === 0) return [];

  const blockNumbers = block.map(t => getSequenceNumber(t)).filter(n => n != null);
  const minInBlock = minNumber != null ? minNumber : (blockNumbers.length > 0 ? Math.min(...blockNumbers) : 1);

  const updates = [];
  for (let i = 0; i < block.length; i++) {
    const correctNumber = minInBlock + i;
    const t = block[i];
    const currentNum = getSequenceNumber(t);
    const currentText = t.text || '';

    if (currentNum === null) {
      // Only add prefix if this task is actually inside a sequential list (block has other sequential tasks).
      // If the block is just this task alone (e.g. new task after non-sequential "d"), don't add a prefix.
      const hasOtherSequentialInBlock = block.some(other => other.id !== t.id && getSequenceNumber(other) != null);
      if (hasOtherSequentialInBlock) {
        const leading = currentText.match(/^(\s*)/)?.[1] || '';
        const withoutNumber = currentText.replace(/^\s*\d+\.\s*/, '').trim() || currentText.trim();
        const newText = leading + correctNumber + '. ' + withoutNumber;
        if (newText !== currentText) {
          updates.push({ id: t.id, text: newText });
        }
      }
      continue;
    }
    if (currentNum === correctNumber) continue;

    const newText = currentText.replace(/^(\s*)\d+\.\s*/, `$1${correctNumber}. `);
    if (newText !== currentText) {
      updates.push({ id: t.id, text: newText });
    }
  }
  return updates;
}

/**
 * After indenting a task under previousSibling, renumbers the remaining sequential siblings
 * at the original level (e.g. 3., 4. → 2., 3.). Uses renumberSequentialListAroundTask with
 * expandLeft: false and minNumber derived from previousSibling.
 *
 * @param {Object} previousSibling - The task we indented under (e.g. "1. A")
 * @param {string|null} originalParentId - The parent_task_id the indented task had before the move
 * @param {Array} tasksAfterIndent - All tasks reflecting state after the indent
 * @returns {Array<{id: string, text: string}>} - Updates for tasks whose number was wrong
 */
export function renumberSequentialListAfterIndent(previousSibling, originalParentId, tasksAfterIndent) {
  const siblings = tasksAfterIndent.filter(t =>
    (originalParentId == null ? t.parent_task_id == null : t.parent_task_id === originalParentId)
  );
  siblings.sort((a, b) => {
    const orderA = a.order != null ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order != null ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  const previousOrder = previousSibling.order != null ? previousSibling.order : Number.MAX_SAFE_INTEGER;
  const firstRemaining = siblings.find(t => (t.order != null ? t.order : Number.MAX_SAFE_INTEGER) > previousOrder);
  if (!firstRemaining || !isSequentialTask(firstRemaining)) return [];
  const startAt = getSequenceNumber(previousSibling) != null ? getSequenceNumber(previousSibling) + 1 : 1;
  return renumberSequentialListAroundTask(firstRemaining, tasksAfterIndent, { expandLeft: false, minNumber: startAt });
}

// Helper function to check if a task is complete (handles tasks with subtasks)
function checkTaskComplete(task) {
  if (task.subtasks && task.subtasks.length > 0) {
    const stats = getSubtaskStats(task);
    return stats.total > 0 && stats.completed === stats.total;
  } else {
    return task.status === 'complete' || task.status === 'wont_do';
  }
}

// Check if a sequential task can be completed (all previous sequential tasks in group are completed)
export function canCompleteSequentialTask(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return true;
  
  if (!isSequentialTask(currentTask)) return true;
  
  const sequentialGroup = getSequentialTaskGroup(taskId, tasks);
  if (sequentialGroup.length === 0) return true;
  
  const currentSequenceNumber = getSequenceNumber(currentTask);
  if (currentSequenceNumber === null) return true;
  
  // Sort sequential group by order (visual order) to find the continuous sequence
  const sortedGroup = [...sequentialGroup].sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  // Find current task's position in the sorted group
  const currentIndex = sortedGroup.findIndex(t => t.id === taskId);
  if (currentIndex === -1) return true;
  
  // Build task tree to get subtasks for each task
  const taskTree = buildTaskTree(tasks);
  
  // Helper to find task with subtasks in tree
  const findTaskInTree = (taskList, targetId) => {
    for (const task of taskList) {
      if (task.id === targetId) {
        return task;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        const found = findTaskInTree(task.subtasks, targetId);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Only check tasks that form a continuous sequence ending at the current task
  // Look backwards from current position to find all preceding tasks in the continuous sequence
  let expectedSequenceNumber = currentSequenceNumber - 1;
  
  for (let i = currentIndex - 1; i >= 0; i--) {
    const task = sortedGroup[i];
    const taskSequenceNumber = getSequenceNumber(task);
    if (taskSequenceNumber === null) continue;
    
    // If we encounter a task with sequence number >= current, we've moved to a different sequence group
    // Stop looking further back
    if (taskSequenceNumber >= currentSequenceNumber) {
      break;
    }
    
    // Only check tasks that form a continuous sequence
    // If we're looking for sequence N, we should find exactly sequence N
    if (taskSequenceNumber === expectedSequenceNumber) {
      // Get the task with its subtasks from the tree
      const taskWithSubtasks = findTaskInTree(taskTree, task.id);
      if (!taskWithSubtasks) {
        // Gap in sequence, stop
        break;
      }
      
      // Check if this previous task is completed (handles tasks with subtasks)
      const taskIsComplete = checkTaskComplete(taskWithSubtasks);
      if (!taskIsComplete) {
        return false;
      }
      
      // Move to next expected sequence number
      expectedSequenceNumber--;
      
      // If we've reached sequence 0, we're done
      if (expectedSequenceNumber < 1) {
        break;
      }
    } else if (taskSequenceNumber < expectedSequenceNumber) {
      // We found a task with a lower sequence number than expected
      // This means there's a gap in the sequence, so stop
      break;
    }
    // If taskSequenceNumber > expectedSequenceNumber but < currentSequenceNumber,
    // we skip it (it's not part of the continuous sequence we're checking)
  }
  
  return true;
}

// Check if a task is blocked because its parent (or any ancestor) is a blocked sequential task
export function isBlockedByParentSequentialTask(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask || !currentTask.parent_task_id) return false;
  
  // Build task tree to get subtasks
  const taskTree = buildTaskTree(tasks);
  
  // Helper to find task with subtasks in tree
  const findTaskInTree = (taskList, targetId) => {
    for (const task of taskList) {
      if (task.id === targetId) {
        return task;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        const found = findTaskInTree(task.subtasks, targetId);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Check all ancestors up the tree
  let parentId = currentTask.parent_task_id;
  while (parentId !== null && parentId !== undefined) {
    const parentTask = tasks.find(t => t.id === parentId);
    if (!parentTask) break;
    
    // Check if parent is a sequential task that is blocked
    if (isSequentialTask(parentTask)) {
      const parentCanComplete = canCompleteSequentialTask(parentId, tasks);
      
      // Get parent task with subtasks to check completion
      const parentWithSubtasks = findTaskInTree(taskTree, parentId);
      const isParentComplete = parentWithSubtasks ? checkTaskComplete(parentWithSubtasks) : false;
      
      if (!parentCanComplete && !isParentComplete) {
        return true; // Parent is a blocked sequential task
      }
    }
    
    // Move up to the next parent
    parentId = parentTask.parent_task_id;
  }
  
  return false;
}

// Check if a task can be completed (considering both its own sequential status and parent blocking)
export function canCompleteTask(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return true;
  
  // Check if blocked by parent sequential task
  if (isBlockedByParentSequentialTask(taskId, tasks)) {
    return false;
  }
  
  // Check if this task itself is a sequential task that can't be completed
  if (isSequentialTask(currentTask)) {
    return canCompleteSequentialTask(taskId, tasks);
  }
  
  return true;
}

// Get blocking tasks for a sequential task (previous incomplete sequential tasks)
function getBlockingTasksForSequentialTask(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask || !isSequentialTask(currentTask)) return [];
  
  const sequentialGroup = getSequentialTaskGroup(taskId, tasks);
  if (sequentialGroup.length === 0) return [];
  
  const currentSequenceNumber = getSequenceNumber(currentTask);
  if (currentSequenceNumber === null) return [];
  
  // Sort sequential group by order (visual order) to find the immediate preceding task
  const sortedGroup = [...sequentialGroup].sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  // Find current task's position in the sorted group
  const currentIndex = sortedGroup.findIndex(t => t.id === taskId);
  if (currentIndex === -1) return [];
  
  const taskTree = buildTaskTree(tasks);
  const findTaskInTree = (taskList, targetId) => {
    for (const task of taskList) {
      if (task.id === targetId) {
        return task;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        const found = findTaskInTree(task.subtasks, targetId);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Look backwards from current position to find all preceding incomplete tasks
  // that form a continuous sequence ending at the current task.
  // We only include tasks if they form a continuous sequence (e.g., if current is 4, we look for 3, then 2, then 1)
  const blockingTasks = [];
  let expectedSequenceNumber = currentSequenceNumber - 1;
  
  for (let i = currentIndex - 1; i >= 0; i--) {
    const task = sortedGroup[i];
    const taskSequenceNumber = getSequenceNumber(task);
    if (taskSequenceNumber === null) continue;
    
    // If we encounter a task with sequence number >= current, we've moved to a different sequence group
    // Stop looking further back
    if (taskSequenceNumber >= currentSequenceNumber) {
      break;
    }
    
    // Only include tasks that form a continuous sequence
    // If we're looking for sequence N, we should find exactly sequence N
    if (taskSequenceNumber === expectedSequenceNumber) {
      const taskWithSubtasks = findTaskInTree(taskTree, task.id);
      if (!taskWithSubtasks) {
        // Gap in sequence, stop
        break;
      }
      
      const isTaskComplete = checkTaskComplete(taskWithSubtasks);
      if (!isTaskComplete) {
        // Add this blocking task (insert at beginning to maintain order)
        blockingTasks.unshift(task.id);
      }
      
      // Move to next expected sequence number
      expectedSequenceNumber--;
      
      // If we've reached sequence 0, we're done
      if (expectedSequenceNumber < 1) {
        break;
      }
    } else if (taskSequenceNumber < expectedSequenceNumber) {
      // We found a task with a lower sequence number than expected
      // This means there's a gap in the sequence, so stop
      break;
    }
    // If taskSequenceNumber > expectedSequenceNumber but < currentSequenceNumber,
    // we skip it (it's not part of the continuous sequence we're building)
  }
  
  return blockingTasks;
}

// Get the tasks that are blocking a given task (tasks that need to be completed first)
export function getBlockingTasks(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return [];
  
  const blockingTasks = [];
  
  // Check if blocked by parent sequential task
  if (currentTask.parent_task_id) {
    let parentId = currentTask.parent_task_id;
    while (parentId !== null && parentId !== undefined) {
      const parentTask = tasks.find(t => t.id === parentId);
      if (!parentTask) break;
      
      if (isSequentialTask(parentTask)) {
        const taskTree = buildTaskTree(tasks);
        const findTaskInTree = (taskList, targetId) => {
          for (const task of taskList) {
            if (task.id === targetId) {
              return task;
            }
            if (task.subtasks && task.subtasks.length > 0) {
              const found = findTaskInTree(task.subtasks, targetId);
              if (found) return found;
            }
          }
          return null;
        };
        
        const parentWithSubtasks = findTaskInTree(taskTree, parentId);
        const isParentComplete = parentWithSubtasks ? checkTaskComplete(parentWithSubtasks) : false;
        const parentCanComplete = canCompleteSequentialTask(parentId, tasks);
        
        if (!parentCanComplete && !isParentComplete) {
          // Get the blocking tasks for the parent
          const parentBlockingTasks = getBlockingTasksForSequentialTask(parentId, tasks);
          blockingTasks.push(...parentBlockingTasks);
          blockingTasks.push(parentId);
          break;
        }
      }
      
      parentId = parentTask.parent_task_id;
    }
  }
  
  // Check if this task itself is a sequential task
  if (isSequentialTask(currentTask)) {
    const sequentialBlockingTasks = getBlockingTasksForSequentialTask(taskId, tasks);
    blockingTasks.push(...sequentialBlockingTasks);
  }
  
  return [...new Set(blockingTasks)]; // Remove duplicates
}
